import { useState } from "react";
import { Badge, EmptyState, Panel, cn, type Tone } from "@/components/ui";
import { openExceptions, verifiedShare } from "@/lib/finance";
import { formatDateTime } from "@/lib/format";
import type { EvidenceKind, EvidenceSource } from "@/lib/types";
import { useCase } from "@/components/case/case-context";
import { ExtractedRow } from "./extracted-row";

/**
 * Evidence merges documents, banking, accounting and external data into one
 * surface. They are filters, not separate screens: an analyst reconciling a
 * debt figure needs the loan agreement and the bank feed side by side, and
 * splitting them into tabs would make that a navigation problem.
 */
const FILTERS: { key: EvidenceKind | "all"; label: string }[] = [
  { key: "all", label: "All" },
  { key: "document", label: "Documents" },
  { key: "banking", label: "Banking" },
  { key: "accounting", label: "Accounting" },
  { key: "external", label: "External Data" },
];

const STATUS_TONE: Record<EvidenceSource["status"], Tone> = {
  received: "neutral",
  missing: "negative",
  stale: "caution",
};

export function EvidenceView() {
  const { evidence } = useCase();
  const [filter, setFilter] = useState<EvidenceKind | "all">("all");

  const shown = filter === "all" ? evidence : evidence.filter((source) => source.kind === filter);
  const exceptions = openExceptions(evidence).length;
  const verified = Math.round(verifiedShare(evidence) * 100);

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="Evidence type">
          {FILTERS.map((entry) => {
            const count =
              entry.key === "all"
                ? evidence.length
                : evidence.filter((source) => source.kind === entry.key).length;
            const active = filter === entry.key;
            return (
              <button
                key={entry.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setFilter(entry.key)}
                className={cn(
                  "rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
                  active
                    ? "border-accent-line bg-accent-soft text-accent"
                    : "border-line bg-surface text-ink-muted hover:bg-surface-sunken hover:text-ink",
                )}
              >
                {entry.label}
                <span className="numeric ml-1.5 text-ink-faint">{count}</span>
              </button>
            );
          })}
        </div>

        <p className="text-xs text-ink-muted">
          <span className="numeric font-medium text-ink">{verified}%</span> of extracted values
          confirmed
          {exceptions > 0 ? (
            <>
              {" · "}
              <span className="numeric font-medium text-caution">{exceptions}</span> open exception
              {exceptions === 1 ? "" : "s"}
            </>
          ) : null}
        </p>
      </div>

      {shown.length === 0 ? (
        <Panel>
          <EmptyState title="No sources of this type" detail="Try another filter." />
        </Panel>
      ) : (
        <div className="space-y-4">
          {shown.map((source) => (
            <SourcePanel key={source.id} source={source} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Source document on the left, what was pulled out of it on the right. The
 * pairing is the point — an extracted figure is never shown without the thing
 * it was extracted from.
 */
function SourcePanel({ source }: { source: EvidenceSource }) {
  return (
    <Panel id={source.id} className="scroll-mt-6 overflow-hidden">
      <div className="grid md:grid-cols-[minmax(0,17rem)_1fr]">
        <div className="border-b border-line bg-surface-sunken p-4 md:border-r md:border-b-0">
          <div className="eyebrow">Source document</div>
          <h3 className="mt-1.5 text-sm font-semibold text-ink">{source.name}</h3>
          <p className="mt-1 text-xs text-ink-muted">{source.provider}</p>

          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <Badge tone={STATUS_TONE[source.status]}>{source.status}</Badge>
            <Badge>{source.kind}</Badge>
            {source.pages ? <Badge>{source.pages} pages</Badge> : null}
          </div>

          <p className="mt-3 text-xs text-ink-faint">
            {source.status === "missing" ? "Requested" : "Received"} {formatDateTime(source.receivedAt)}
          </p>
        </div>

        <div>
          <div className="flex items-center justify-between border-b border-line px-4 py-2">
            <span className="eyebrow">Extracted data</span>
            <span className="numeric text-xs text-ink-faint">{source.extracted.length}</span>
          </div>

          {source.extracted.length === 0 ? (
            <EmptyState
              title="Nothing extracted yet"
              detail={
                source.status === "missing"
                  ? "This document has not been provided. Nothing can be extracted until it arrives."
                  : "Extraction has not run against this source."
              }
            />
          ) : (
            source.extracted.map((value) => (
              <ExtractedRow key={value.id} value={value} sourceName={source.name} />
            ))
          )}
        </div>
      </div>
    </Panel>
  );
}
